/**
 * Auth — three ways in (PRD 04 §3: an email link, an email + password, or Google), and the
 * `authorIdFor(uid)` seam.
 *
 * Three things live here and nowhere else:
 *
 * 1. **The session.** `onIdTokenChanged`, not `onAuthStateChanged`, because the `admin`
 *    claim is minted into the token and we want a refresh to be seen.
 * 2. **The author id.** Everything downstream addresses `authors/{authorId}/…`, and
 *    `authorIdFor(uid)` from the engine is the single place that mapping happens. Today it
 *    is identity; when the Studio grows teams, that function absorbs it (PRD 05 §3).
 * 3. **The `admin` claim**, read off the ID token into context. God mode (`studio/06`) is
 *    its only consumer; keeping the read behind one hook is what lets a non-admin build
 *    contain no God-mode code path at all.
 *
 * One account per email (Firebase's default). Every method here is a plain SDK call with
 * three house rules around it: Google is a popup and **never a redirect** (a redirect loses
 * in-page state on tablet and desktop alike); an address that already has a way in is
 * **never linked silently** — the error carries the address and the sign-in page offers the
 * method; and a password account is **never blocked** on verifying its email — the product
 * is parent-gated and nothing child-facing depends on it.
 *
 * **The three mails are not sent from here** (`general/08`). The sign-in link, the verification
 * and the reset go through the `requestAuthMail` callable, which generates the same Firebase
 * link the SDK used to and sends our own template from `MAIL.from` (`@atlas-nook/brand`, the
 * one place that address is written down). What this file still does is everything around
 * them: park the address, finish the link on arrival, and pass the UI language so the mail
 * arrives in it.
 */

import { authorIdFor } from '@atlas-nook/story';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  isSignInWithEmailLink,
  linkWithCredential,
  onIdTokenChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  updatePassword,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import { firebase } from '../firebase.js';
import { useLocale } from '../i18n/I18nProvider.js';
import { AccountExistsError, authErrorCode } from './errors.js';

/** Where the email is parked between "send me a link" and opening it. */
const EMAIL_KEY = 'atlas-nook-studio:signin-email';

/**
 * How an account comes in. `email` is the honest label when Firebase will not say which of
 * link or password it is — the lookup is disabled by email-enumeration protection.
 */
export type SignInMethod = 'link' | 'password' | 'google' | 'email';

export interface Session {
  user: User;
  /** `authors/{authorId}` — the producer inside the Studio. Never assume it equals `uid`. */
  authorId: string;
  email: string;
  /** Initials for the avatar. The Studio's chrome shows no one's photograph. */
  initials: string;
  /** True only if the ID token carries it. Read once here; used by God mode (`studio/06`). */
  admin: boolean;
  /** The ways this account signs in — informational, for the account menu. */
  methods: SignInMethod[];
  /** False for a password account that has not opened its verification link yet. */
  emailVerified: boolean;
}

interface AuthValue {
  /** `undefined` while the first token resolves; `null` once we know nobody is signed in. */
  session: Session | null | undefined;
  sendLink: (email: string) => Promise<void>;
  completeLink: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  /** Creates the account, sends the verification email, and signs in without waiting for it. */
  createAccount: (email: string, password: string) => Promise<void>;
  /** Resolves whether or not the address has an account — the page says "sent" either way. */
  resetPassword: (email: string) => Promise<void>;
  /** A popup, `select_account` every time, and never a redirect. */
  signInWithGoogle: () => Promise<void>;
  sendVerification: () => Promise<void>;
  /** A link or Google account's first password. Re-auth-gated by Firebase's own rule. */
  setPassword: (next: string) => Promise<void>;
  /** A password account's new password, proven with the current one. */
  changePassword: (current: string, next: string) => Promise<void>;
  signOutNow: () => Promise<void>;
  /** The email we asked for a link with, if this browser is the one that asked. */
  pendingEmail: string | null;
  /** True when the current URL is a sign-in link waiting to be completed. */
  isLinkArrival: boolean;
  /** The quiet "verify your email" note, until it is dismissed for this visit. */
  verifyNoteDismissed: boolean;
  dismissVerifyNote: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

function initialsFor(user: User): string {
  const source = user.displayName ?? user.email ?? '';
  const words = source.split(/[\s@._-]+/).filter((word) => word !== '');
  const letters = words.slice(0, 2).map((word) => word[0] ?? '');
  return (letters.join('') || '?').toUpperCase();
}

function readStoredEmail(): string | null {
  try {
    return window.localStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
}

function storeEmail(email: string | null): void {
  try {
    if (email === null) window.localStorage.removeItem(EMAIL_KEY);
    else window.localStorage.setItem(EMAIL_KEY, email);
  } catch {
    // Without storage the link still works — it just asks for the email again on arrival.
  }
}

function hasProvider(user: User, providerId: string): boolean {
  return user.providerData.some((info) => info.providerId === providerId);
}

/**
 * The ways an account comes in.
 *
 * `providerData` says *Google* plainly, but files a link account and a password account
 * under the same `password` provider. The lookup tells them apart — unless enumeration
 * protection has switched it off, in which case the honest answer is "email".
 */
async function methodsFor(
  lookup: (email: string) => Promise<string[]>,
  user: User,
): Promise<SignInMethod[]> {
  const methods: SignInMethod[] = [];
  if (hasProvider(user, 'google.com')) methods.push('google');

  if (hasProvider(user, 'password') || user.providerData.length === 0) {
    let known: string[] = [];
    if (user.email !== null) {
      try {
        known = await lookup(user.email);
      } catch {
        known = [];
      }
    }
    if (known.includes('password')) methods.push('password');
    if (known.includes('emailLink')) methods.push('link');
    if (!known.includes('password') && !known.includes('emailLink')) methods.push('email');
  }
  return methods;
}

/**
 * The mail callable, reached lazily.
 *
 * A static import would put the Functions SDK — its own island (`studio/11`) — into the entry
 * chunk, on the sign-in page, where the first paint is auth only. By the time a parent presses
 * *Send me a link* the shell is painted and one more chunk is nothing.
 */
async function requestAuthMail(request: {
  kind: 'signInLink' | 'verify' | 'reset';
  email: string;
  lang: 'en' | 'tr';
}): Promise<void> {
  const { requestAuthMailCall } = await import('../data/callables.js');
  await requestAuthMailCall({ ...request, app: 'studio' });
}

function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  // The account picker, every time: a shared tablet must not slide into the last account.
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const { auth } = firebase();
  // The mail arrives in the language the page is in; the backend renders both.
  const lang = useLocale();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [pendingEmail, setPendingEmail] = useState<string | null>(() => readStoredEmail());
  const [isLinkArrival, setIsLinkArrival] = useState(false);
  const [verifyNoteDismissed, setVerifyNoteDismissed] = useState(false);
  // Bumped after a password is set or changed, so the menu's method list follows.
  const [methodsVersion, setMethodsVersion] = useState(0);

  useEffect(() => {
    setIsLinkArrival(isSignInWithEmailLink(auth, window.location.href));
  }, [auth]);

  useEffect(
    () =>
      onIdTokenChanged(auth, (user) => {
        if (user === null) {
          setSession(null);
          setVerifyNoteDismissed(false);
          return;
        }
        void Promise.all([
          user.getIdTokenResult(),
          methodsFor((email) => fetchSignInMethodsForEmail(auth, email), user),
        ]).then(([token, methods]) => {
          setSession({
            user,
            authorId: authorIdFor(user.uid),
            email: user.email ?? '',
            initials: initialsFor(user),
            admin: token.claims['admin'] === true,
            methods,
            emailVerified: user.emailVerified,
          });
        });
      }),
    [auth, methodsVersion],
  );

  const sendLink = useCallback(
    async (email: string) => {
      // The link comes back to the Studio's door (`/signin`) and the app finishes the sign-in
      // itself — the backend builds that URL from brand, never from here.
      await requestAuthMail({ kind: 'signInLink', email, lang });
      storeEmail(email);
      setPendingEmail(email);
    },
    [lang],
  );

  const completeLink = useCallback(
    async (email: string) => {
      await signInWithEmailLink(auth, email, window.location.href);
      storeEmail(null);
      setPendingEmail(null);
      setIsLinkArrival(false);
      // Drop the one-time credential out of the address bar.
      window.history.replaceState(null, '', '/');
    },
    [auth],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    [auth],
  );

  const createAccount = useCallback(
    async (email: string, password: string) => {
      await createUserWithEmailAndPassword(auth, email, password);
      // Sent, not awaited on: the account is in whether or not this email arrives, and a
      // failure here is not a reason to refuse the sign-in that just succeeded. The caller is
      // signed in by now, which is what `verify` requires — it mails the caller's own address.
      await requestAuthMail({ kind: 'verify', email, lang }).catch(() => undefined);
    },
    [auth, lang],
  );

  const resetPassword = useCallback(
    async (email: string) => {
      // The backend answers the same way whether or not the address has an account: a reset
      // that names which addresses exist is a directory. What can still fail here is the
      // sending itself, and that reaches the page as "that didn't work".
      await requestAuthMail({ kind: 'reset', email, lang });
    },
    [lang],
  );

  const signInWithGoogle = useCallback(async () => {
    let result;
    try {
      result = await signInWithPopup(auth, googleProvider());
    } catch (error) {
      if (authErrorCode(error) === 'auth/account-exists-with-different-credential') {
        const email = (error as { customData?: { email?: string } }).customData?.email ?? '';
        let methods: string[];
        try {
          methods = email === '' ? [] : await fetchSignInMethodsForEmail(auth, email);
        } catch {
          methods = [];
        }
        throw new AccountExistsError(email, methods);
      }
      throw error;
    }

    /**
     * The name Google gave us, offered to the profile doc — on **every** Google sign-in, not
     * only a brand-new account (`studio/07` q2, ruled in DEFERRED). A parent who signed up
     * with an email link and later pressed *Continue with Google* used to stay nameless;
     * `writeDisplayNameIfUnset` reads once and never overwrites a name they set themselves.
     *
     * **Imported here rather than at the top**, and that is load-bearing rather than tidy:
     * `data/writes.js` reaches Firestore, and a static import would put the whole Firestore
     * SDK in the entry chunk — on the sign-in page, where there is nothing to read yet
     * (`studio/11`). By the time this line runs the parent is signed in and a surface is
     * about to ask for the same module anyway.
     *
     * Best effort either way: a profile line is not worth failing a sign-in over.
     */
    const name = result.user.displayName;
    if (name !== null && name !== '') {
      await import('../data/writes.js')
        .then(({ writeDisplayNameIfUnset }) => writeDisplayNameIfUnset(result.user.uid, name))
        .catch(() => undefined);
    }
  }, [auth]);

  const sendVerification = useCallback(async () => {
    const user = auth.currentUser;
    if (user === null || user.email === null) return;
    await requestAuthMail({ kind: 'verify', email: user.email, lang });
  }, [auth, lang]);

  const setPassword = useCallback(
    async (next: string) => {
      const user = auth.currentUser;
      if (user === null || user.email === null) throw new Error('setPassword: not signed in');

      const attempt = async (): Promise<void> => {
        if (hasProvider(user, 'password')) {
          // A link account already carries the `password` provider; its first password is
          // an update, not a link (linking would say `provider-already-linked`).
          await updatePassword(user, next);
          return;
        }
        try {
          await linkWithCredential(user, EmailAuthProvider.credential(user.email ?? '', next));
        } catch (error) {
          // The credential names this account's own address, so "already in use" can only
          // mean *this* account — the Auth emulator answers the link that way (production
          // links). `updatePassword` adds the password provider just the same.
          const code = authErrorCode(error);
          if (code !== 'auth/email-already-in-use' && code !== 'auth/provider-already-linked') {
            throw error;
          }
          await updatePassword(user, next);
        }
      };

      try {
        await attempt();
      } catch (error) {
        // Firebase's own gate: a fresh sign-in first. Google can prove one with a popup; a
        // link account cannot without a new link, so its error reaches the dialog as a line.
        if (
          authErrorCode(error) !== 'auth/requires-recent-login' ||
          !hasProvider(user, 'google.com')
        ) {
          throw error;
        }
        await reauthenticateWithPopup(user, googleProvider());
        await attempt();
      }
      setMethodsVersion((version) => version + 1);
    },
    [auth],
  );

  const changePassword = useCallback(
    async (current: string, next: string) => {
      const user = auth.currentUser;
      if (user === null || user.email === null) throw new Error('changePassword: not signed in');
      // The current password is the recent sign-in Firebase asks for, every time.
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, next);
      setMethodsVersion((version) => version + 1);
    },
    [auth],
  );

  const signOutNow = useCallback(async () => {
    await signOut(auth);
    storeEmail(null);
    setPendingEmail(null);
  }, [auth]);

  const dismissVerifyNote = useCallback(() => setVerifyNoteDismissed(true), []);

  const value = useMemo<AuthValue>(
    () => ({
      session,
      sendLink,
      completeLink,
      signInWithPassword,
      createAccount,
      resetPassword,
      signInWithGoogle,
      sendVerification,
      setPassword,
      changePassword,
      signOutNow,
      pendingEmail,
      isLinkArrival,
      verifyNoteDismissed,
      dismissVerifyNote,
    }),
    [
      session,
      sendLink,
      completeLink,
      signInWithPassword,
      createAccount,
      resetPassword,
      signInWithGoogle,
      sendVerification,
      setPassword,
      changePassword,
      signOutNow,
      pendingEmail,
      isLinkArrival,
      verifyNoteDismissed,
      dismissVerifyNote,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth: no AuthProvider above this component');
  return value;
}

/** The signed-in session, or a thrown error. For anything under the authenticated layout. */
export function useSession(): Session {
  const { session } = useAuth();
  if (session === undefined || session === null) {
    throw new Error('useSession: no signed-in session — this component is outside RequireAuth');
  }
  return session;
}

/**
 * The **one** place the `admin` claim is read.
 *
 * God mode (`studio/06`) is the only consumer there will be, and it gates on this so a
 * non-admin's Studio has no overlay, no listener, and no query — not a disabled button, an
 * absent one (PRD 04 §8).
 */
export function useAdminClaim(): boolean {
  const { session } = useAuth();
  return session?.admin === true;
}
